import type { Price } from '$lib/components/types';
import type { SearchParamsType } from '$lib/services/urls/searchParams';
import type {
	AccommodationMichelinPoiData,
	AccommodationPoiData,
	DateRange,
	MichelinDistinction,
	RestaurantMichelinPoiData,
	RestStopPoiData,
	Review,
	TourismPoiData,
} from './types';
import { page } from '$app/stores';
import * as c from '$lib/i18n/computedMessages';
import { transformStringDateToCalendarDate } from '$lib/utils/date';
import { formatPrice } from '$lib/utils/formatPrice';
import { type BookingParams, getBookingUrl } from '$lib/utils/getBookingUrl';
import * as m from '$paraglide/messages';
import { languageTag } from '$paraglide/runtime';
import { get } from 'svelte/store';

export function buildAccommodationPriceText(
	data: AccommodationPoiData | AccommodationMichelinPoiData,
): string | undefined {
	if (data.isAvailable === false) {
		return m.poi_card__non_availability();
	}
	return data.startingPrice
		? m.poi_card__min_price({
				min: formatPrice({
					amount: data.startingPrice.amount,
					currency: data.startingPrice.currency,
					language: languageTag(),
				}),
			})
		: undefined;
}

export function buildAveragePriceText(averagePrice: Price | null | undefined): string | undefined {
	return averagePrice
		? m.poi_card__average_price({
				average: formatPrice({
					amount: averagePrice.amount,
					currency: averagePrice.currency,
					language: languageTag(),
				}),
			})
		: undefined;
}

export function buildReviewText(review: Review | null | undefined): string | undefined {
	return review
		? `${c.getReviewMentionTranslation(review.mention)} ${review.score}${
			review.count ? ` (${m.poi_card__review_count({ count: review.count })})` : ''
		}`
		: undefined;
}

export function buildBookingUrl(data: AccommodationPoiData, searchParams?: SearchParamsType) {
	const { bookUrl, subType, id: poiId, srcPoiId, stars, startingPrice } = data;
	const departure = searchParams?.itinerary?.at(0);
	const arrival = searchParams?.itinerary?.at(-1);

	const { startDate, endDate } = searchParams?.filters?.ACCOMMODATION_AVAILABILITY_DATE_RANGE as DateRange || {};
	const { pagename: pageName } = get(page).data;

	const checkin = transformStringDateToCalendarDate(startDate);
	const checkout = transformStringDateToCalendarDate(endDate);

	const priceMinAccommodationWithCurrency = startingPrice
		? formatPrice({
				amount: startingPrice.amount,
				currency: startingPrice.currency,
				language: languageTag(),
			})
		: '';

	const options: BookingParams = {
		pageName,
		contentType: 'poilist',
		star: stars || undefined,
		arrival: arrival?.label || searchParams?.selectedAddress?.address || '',
		departure: departure?.label || '',
		distance: '',
		bookUrl,
		priceMinAccommodationWithCurrency,
		subType,
		poiId,
		srcPoiId,
		checkin,
		checkout,
	};

	return getBookingUrl(options);
}

export function buildAccommodationMichelinDistinction(
	data: AccommodationMichelinPoiData,
): MichelinDistinction | undefined {
	if (
		data.distinctionStars
		&& data.distinctionDescription
		&& data.distinctionStars >= 1
		&& data.distinctionStars <= 3
	) {
		return {
			icon: 'michelin-key',
			amount: data.distinctionStars,
			label: data.distinctionDescription,
		};
	}
	return undefined;
}

export function buildRestaurantMichelinDistinction(
	data: RestaurantMichelinPoiData,
): MichelinDistinction | undefined {
	if (
		data.distinctionStars
		&& data.distinctionDescription
		&& data.distinctionStars >= 1
		&& data.distinctionStars <= 3
	) {
		return {
			icon: 'michelin-star',
			amount: data.distinctionStars,
			label: data.distinctionDescription,
		};
	}
	if (data.bibGourmand && data.distinctionDescription) {
		return {
			icon: 'bib-gourmand',
			amount: 1,
			label: data.distinctionDescription,
		};
	}
	return undefined;
}

export function buildTourismMichelinDistinction(
	data: TourismPoiData,
): MichelinDistinction | undefined {
	if (
		data.distinctionStars
		&& data.distinctionDescription
		&& data.distinctionStars >= 1
		&& data.distinctionStars <= 3
	) {
		return {
			icon: 'tourism-star',
			amount: data.distinctionStars,
			label: data.distinctionDescription,
		};
	}
	return undefined;
}

export function getRestStopHeadlineText(poi: RestStopPoiData) {
	if (poi.isFamilyFriendly) {
		if (poi.isVillageEtape) {
			return m.kleber__village__description();
		}
		return m.kleber__restStop__description();
	}
}
